import { notification } from "antd";
import instance from "api/cmsapi";
import { useMutation, useQuery, useQueryClient } from "react-query";

/* =========================================================
   API Requests OHLGS KPA Setup
   ========================================================= */

// CREATE
export const createKPA = async (payload) =>
  await instance.post("ohlgs-kpa", payload);

// CREATE BULK
export const createKPAs = async (payload) =>
  await instance.post("ohlgs-kpa/bulk", payload);

// UPDATE
export const updateKPA = async ({ id, payload }) =>
  await instance.put(`ohlgs-kpa/${id}`, payload);

// GET ALL
export const getAllKPAs = async () =>
  await instance.get("ohlgs-kpa");

// GET BY ID
export const getKPAById = async (id) =>
  await instance.get(`ohlgs-kpa/${id}`);

// GET BY TYPE
export const getKPAByType = async (kpaType) =>
  await instance.get(`ohlgs-kpa/type/${kpaType}`);

/* =========================================================
   React Query Hooks OHLGS KPA Setup
   ========================================================= */

// GET ALL
export function useGetAllKPAs() {
  return useQuery(
    ["getAllKPAs"],
    getAllKPAs,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPAs",
        }),
    }
  );
}

// GET BY ID
export function useGetKPAById(id) {
  return useQuery(
    ["getKPAById", id],
    () => getKPAById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPA",
        }),
    }
  );
}

// GET BY TYPE
export function useGetKPAByType(kpaType) {
  return useQuery(
    ["getKPAByType", kpaType],
    () => getKPAByType(kpaType),
    {
      enabled: !!kpaType,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPAs by type",
        }),
    }
  );
}

// CREATE
export function useCreateKPA() {
  const queryClient = useQueryClient();

  return useMutation(createKPA, {
    onSuccess: () => {
      notification.success({
        message: "KPA created successfully",
      });

      queryClient.invalidateQueries(["getAllKPAs"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating KPA",
      }),
  });
}

// CREATE BULK
export function useCreateKPAs() {
  const queryClient = useQueryClient();

  return useMutation(createKPAs, {
    onSuccess: () => {
      notification.success({
        message: "KPAs created successfully",
      });

      queryClient.invalidateQueries(["getAllKPAs"]);
      queryClient.invalidateQueries(["getKPAByType"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating KPAs",
      }),
  });
}

// UPDATE
export function useUpdateKPA() {
  const queryClient = useQueryClient();

  return useMutation(updateKPA, {
    onSuccess: () => {
      notification.success({
        message: "KPA updated successfully",
      });

      queryClient.invalidateQueries(["getAllKPAs"]);
      queryClient.invalidateQueries(["getKPAById"]);
      queryClient.invalidateQueries(["getKPAByType"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating KPA",
      }),
  });
}

/* =========================================================
   API Requests KPISetup
   ========================================================= */

// CREATE KPI SETUP
export const createKPISetup = async (payload) =>
  await instance.post("kpi", payload);

// CREATE KPI SETUP BULK
export const createKPISetups = async (payload) =>
  await instance.post("kpi/bulk", payload);

// UPDATE KPI SETUP
export const updateKPISetup = async ({ id, payload }) =>
  await instance.put(`kpi/${id}`, payload);

// GET ALL KPI SETUPS
export const getAllKPISetups = async () =>
  await instance.get("kpi");

// GET KPI SETUP BY ID
export const getKPISetupById = async (id) =>
  await instance.get(`kpi/${id}`);

// GET KPI SETUP BY KPA ID AND YEAR
export const getKPISetupByKpaAndYear = async ({ kpaId, year }) =>
  await instance.get(`kpi/kpa/${kpaId}/year/${year}`);

// GET KPI SETUP BY KPA ID
export const getKPISetupByKpaId = async (kpaId) =>
  await instance.get(`kpi/kpa/${kpaId}`);

// GET KPI SETUP BY YEAR
export const getKPISetupByYear = async (year) =>
  await instance.get(`kpi/year/${year}`);

// GET KPI SETUP BY STATUS
export const getKPISetupByStatus = async (status) =>
  await instance.get(`kpi/status/${status}`);

export const getKPISetupByYearAndLocationType = async ({ year, locationType }) =>
  await instance.get(`kpi/year/${year}/location-type/${locationType}`);

export const getKPISetupByYearAndKPA = async ({ year, kpaId }) =>
  await instance.get(`kpi/year/${year}/kpa/${kpaId}`);

/* =========================================================
   React Query Hooks KPISetup
   ========================================================= */

// GET ALL KPI SETUPS
export function useGetAllKPISetups() {
  return useQuery(
    ["getAllKPISetups"],
    getAllKPISetups,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups",
        }),
    }
  );
}

// GET KPI SETUP BY ID
export function useGetKPISetupById(id) {
  return useQuery(
    ["getKPISetupById", id],
    () => getKPISetupById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setup",
        }),
    }
  );
}

// GET KPI SETUP BY KPA ID AND YEAR
export function useGetKPISetupByKpaAndYear(params) {
  return useQuery(
    ["getKPISetupByKpaAndYear", params],
    () => getKPISetupByKpaAndYear(params),
    {
      enabled: !!params?.kpaId && !!params?.year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups by KPA and year",
        }),
    }
  );
}

// GET KPI SETUP BY KPA ID
export function useGetKPISetupByKpaId(kpaId) {
  return useQuery(
    ["getKPISetupByKpaId", kpaId],
    () => getKPISetupByKpaId(kpaId),
    {
      enabled: !!kpaId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups by KPA",
        }),
    }
  );
}

// GET KPI SETUP BY YEAR
export function useGetKPISetupByYear(year) {
  return useQuery(
    ["getKPISetupByYear", year],
    () => getKPISetupByYear(year),
    {
      enabled: !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups by year",
        }),
    }
  );
}

// GET KPI SETUP BY STATUS
export function useGetKPISetupByStatus(status) {
  return useQuery(
    ["getKPISetupByStatus", status],
    () => getKPISetupByStatus(status),
    {
      enabled: !!status,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups by status",
        }),
    }
  );
}

// CREATE KPI SETUP
export function useCreateKPISetup() {
  const queryClient = useQueryClient();

  return useMutation(createKPISetup, {
    onSuccess: () => {
      notification.success({
        message: "KPI setup created successfully",
      });

      queryClient.invalidateQueries(["getKPISetupByYearAndKPA"]);
      // queryClient.invalidateQueries(["getKPISetupByKpaAndYear"]);
      // queryClient.invalidateQueries(["getKPISetupByKpaId"]);
      // queryClient.invalidateQueries(["getKPISetupByYear"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating KPI setup",
      }),
  });
}

// CREATE KPI SETUP BULK
export function useCreateKPISetups() {
  const queryClient = useQueryClient();

  return useMutation(createKPISetups, {
    onSuccess: () => {
      notification.success({
        message: "KPI setups created successfully",
      });

      queryClient.invalidateQueries(["getAllKPISetups"]);
      queryClient.invalidateQueries(["getKPISetupByKpaAndYear"]);
      queryClient.invalidateQueries(["getKPISetupByKpaId"]);
      queryClient.invalidateQueries(["getKPISetupByYear"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating KPI setups",
      }),
  });
}

// UPDATE KPI SETUP
export function useUpdateKPISetup() {
  const queryClient = useQueryClient();

  return useMutation(updateKPISetup, {
    onSuccess: () => {
      notification.success({
        message: "KPI setup updated successfully",
      });

      queryClient.invalidateQueries(["getKPISetupByYearAndKPA"]);
      // queryClient.invalidateQueries(["getKPISetupById"]);
      // queryClient.invalidateQueries(["getKPISetupByKpaAndYear"]);
      // queryClient.invalidateQueries(["getKPISetupByKpaId"]);
      // queryClient.invalidateQueries(["getKPISetupByYear"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating KPI setup",
      }),
  });
}

export function useGetKPISetupByYearAndLocationType(params) {
  return useQuery(
    ["getKPISetupByYearAndLocationType", params?.year, params?.locationType],
    () => getKPISetupByYearAndLocationType(params),
    {
      enabled: !!params?.year && !!params?.locationType,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching KPI setups by year and location type",
        }),
    }
  );
}

export function useGetKPISetupByYearAndKPA(params) {
  return useQuery(
    ["getKPISetupByYearAndKPA", params?.year, params?.kpaId],
    () => getKPISetupByYearAndKPA(params),
    {
      enabled: !!params?.year && !!params?.kpaId,
      onError: (error) =>
        notification.warning({
          message:
            error?.response?.data?.message ||
            "No KPI setups found for the selected year and KPA",
        }),
    }
  );
}

/* =========================================================
   API Requests Contract Performance Assessment
   ========================================================= */

// CREATE
export const createContractPerformanceAssessment = async (payload) =>
  await instance.post("contract-performance-assessment", payload);

// CREATE BULK
export const createContractPerformanceAssessments = async (payload) =>
  await instance.post("contract-performance-assessment/bulk", payload);

// UPDATE
export const updateContractPerformanceAssessment = async ({ id, payload }) =>
  await instance.put(`contract-performance-assessment/${id}`, payload);

// GET ALL
export const getAllContractPerformanceAssessments = async () =>
  await instance.get("contract-performance-assessment");

// GET BY ID
export const getContractPerformanceAssessmentById = async (id) =>
  await instance.get(`contract-performance-assessment/${id}`);

// GET BY STATUS
export const getContractPerformanceAssessmentByStatus = async (status) =>
  await instance.get(`contract-performance-assessment/status/${status}`);

// GET BY KPI ID (updated from kpa to kpi)
export const getContractPerformanceAssessmentByKpiId = async (kpiId) =>
  await instance.get(`contract-performance-assessment/kpi/${kpiId}`);

// GET BY KPI ID AND LOCATION (new endpoint)
export const getContractPerformanceAssessmentByKpiIdAndLocation = async ({ kpiId, locationType, locationCode }) =>
  await instance.get(`contract-performance-assessment/kpi/${kpiId}/location`, {
    params: { locationType, locationCode }
  });

// GET BY LOCATION (new endpoint)
export const getContractPerformanceAssessmentByLocation = async ({ locationType, locationCode }) =>
  await instance.get(`contract-performance-assessment/location`, {
    params: { locationType, locationCode }
  });

// GET BY YEAR AND LOCATION (updated)
export const getContractPerformanceAssessmentByYearAndLocation = async ({ year, locationType, locationCode }) =>
  await instance.get(`contract-performance-assessment/search/by-year-and-location`, {
    params: { year, locationType, locationCode }
  });

// CONSOLIDATED ASSESSMENTS - Get by year (path param)
export const getConsolidatedAssessmentsByYear = async (year) =>
  await instance.get(`contract-performance-assessment/consolidated/${year}`);

// CONSOLIDATED ASSESSMENTS - Get by year (query param)
export const getConsolidatedAssessmentsByYearParam = async (year) =>
  await instance.get(`contract-performance-assessment/consolidated`, {
    params: { year }
  });

// GET BY YEAR AND PARENT ID (region code)
export const getAssessmentsByYearAndParentId = async ({ year, parentId }) =>
  await instance.get(`contract-performance-assessment/year/${year}/parent/${parentId}`);

// GET DISTRICT ASSESSMENTS BY REGION (alias for the above)
export const getDistrictAssessmentsByRegion = async ({ year, regionCode }) =>
  await instance.get(`contract-performance-assessment/year/${year}/parent/${regionCode}`);

/* =========================================================
   React Query Hooks Contract Performance Assessment
   ========================================================= */

// GET ALL
export function useGetAllContractPerformanceAssessments() {
  return useQuery(
    ["getAllContractPerformanceAssessments"],
    getAllContractPerformanceAssessments,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching contract performance assessments",
        }),
    }
  );
}

// GET BY ID
export function useGetContractPerformanceAssessmentById(id) {
  return useQuery(
    ["getContractPerformanceAssessmentById", id],
    () => getContractPerformanceAssessmentById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching contract performance assessment",
        }),
    }
  );
}

// GET BY STATUS
export function useGetContractPerformanceAssessmentByStatus(status) {
  return useQuery(
    ["getContractPerformanceAssessmentByStatus", status],
    () => getContractPerformanceAssessmentByStatus(status),
    {
      enabled: !!status,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by status",
        }),
    }
  );
}

// GET BY KPI ID (updated)
export function useGetContractPerformanceAssessmentByKpiId(kpiId) {
  return useQuery(
    ["getContractPerformanceAssessmentByKpiId", kpiId],
    () => getContractPerformanceAssessmentByKpiId(kpiId),
    {
      enabled: !!kpiId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by KPI",
        }),
    }
  );
}

// GET BY KPI ID AND LOCATION (new)
export function useGetContractPerformanceAssessmentByKpiIdAndLocation(params) {
  return useQuery(
    ["getContractPerformanceAssessmentByKpiIdAndLocation", params],
    () => getContractPerformanceAssessmentByKpiIdAndLocation(params),
    {
      enabled: !!params?.kpiId && !!params?.locationType && !!params?.locationCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by KPI and location",
        }),
    }
  );
}

// GET BY LOCATION (new)
export function useGetContractPerformanceAssessmentByLocation(params) {
  return useQuery(
    ["getContractPerformanceAssessmentByLocation", params],
    () => getContractPerformanceAssessmentByLocation(params),
    {
      enabled: !!params?.locationType && !!params?.locationCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by location",
        }),
    }
  );
}

// GET BY YEAR AND LOCATION (updated)
export function useGetContractPerformanceAssessmentByYearAndLocation(params) {
  return useQuery(
    ["getContractPerformanceAssessmentByYearAndLocation", params],
    () => getContractPerformanceAssessmentByYearAndLocation(params),
    {
      enabled: !!params?.year && !!params?.locationType && !!params?.locationCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by year and location",
        }),
    }
  );
}

// CREATE
export function useCreateContractPerformanceAssessment() {
  const queryClient = useQueryClient();

  return useMutation(createContractPerformanceAssessment, {
    onSuccess: () => {
      notification.success({
        message: "Contract performance assessment created successfully",
      });

      queryClient.invalidateQueries(["getAllContractPerformanceAssessments"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByStatus"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByKpiId"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByLocation"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating contract performance assessment",
      }),
  });
}

// CREATE BULK
export function useCreateContractPerformanceAssessments() {
  const queryClient = useQueryClient();

  return useMutation(createContractPerformanceAssessments, {
    onSuccess: () => {
      notification.success({
        message: "Contract performance assessments created successfully",
      });

      queryClient.invalidateQueries(["getAllContractPerformanceAssessments"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByStatus"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByKpiId"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByLocation"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating contract performance assessments",
      }),
  });
}

// UPDATE
export function useUpdateContractPerformanceAssessment() {
  const queryClient = useQueryClient();

  return useMutation(updateContractPerformanceAssessment, {
    onSuccess: () => {
      notification.success({
        message: "Contract performance assessment updated successfully",
      });

      queryClient.invalidateQueries(["getAllContractPerformanceAssessments"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentById"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByStatus"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByKpiId"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByLocation"]);
      queryClient.invalidateQueries(["getContractPerformanceAssessmentByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating contract performance assessment",
      }),
  });
}

// GET CONSOLIDATED ASSESSMENTS BY YEAR (path param)
export function useGetConsolidatedAssessmentsByYear(year) {
  return useQuery(
    ["getConsolidatedAssessmentsByYear", year],
    () => getConsolidatedAssessmentsByYear(year),
    {
      enabled: !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching consolidated assessments by year",
        }),
    }
  );
}

// GET CONSOLIDATED ASSESSMENTS BY YEAR (query param)
export function useGetConsolidatedAssessmentsByYearParam(year) {
  return useQuery(
    ["getConsolidatedAssessmentsByYearParam", year],
    () => getConsolidatedAssessmentsByYearParam(year),
    {
      enabled: !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching consolidated assessments",
        }),
    }
  );
}

// GET ASSESSMENTS BY YEAR AND PARENT ID
export function useGetAssessmentsByYearAndParentId(params) {
  return useQuery(
    ["getAssessmentsByYearAndParentId", params],
    () => getAssessmentsByYearAndParentId(params),
    {
      enabled: !!params?.year && !!params?.parentId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessments by year and parent",
        }),
    }
  );
}

// GET DISTRICT ASSESSMENTS BY REGION
export function useGetDistrictAssessmentsByRegion(params) {
  return useQuery(
    ["getDistrictAssessmentsByRegion", params],
    () => getDistrictAssessmentsByRegion(params),
    {
      enabled: !!params?.year && !!params?.regionCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching district assessments by region",
        }),
    }
  );
}

/* =========================================================
   API Requests Write Up
   ========================================================= */

// CREATE
export const createWriteUp = async (payload) =>
  await instance.post("write-ups", payload);

// CREATE BULK
export const createWriteUps = async (payload) =>
  await instance.post("write-ups/bulk", payload);

// UPDATE
export const updateWriteUp = async ({ id, payload }) =>
  await instance.put(`write-ups/${id}`, payload);

// GET ALL
export const getAllWriteUps = async () =>
  await instance.get("write-ups");

// GET BY ID
export const getWriteUpById = async (id) =>
  await instance.get(`write-ups/${id}`);

// GET BY USERNAME
export const getWriteUpByUsername = async (username) =>
  await instance.get(`write-ups/username/${username}`);

// GET BY USER ROLE
export const getWriteUpByUserRole = async (userRole) =>
  await instance.get(`write-ups/role/${userRole}`);

// GET BY TYPE
export const getWriteUpByType = async (type) =>
  await instance.get(`write-ups/type/${type}`);

// GET BY LOCATION CODE
export const getWriteUpByLocationCode = async (locationCode) =>
  await instance.get(`write-ups/location/${locationCode}`);

// GET BY YEAR
export const getWriteUpByYear = async (year) =>
  await instance.get(`write-ups/year/${year}`);

// GET BY YEAR AND LOCATION
export const getWriteUpByYearAndLocation = async ({ year, locationCode }) =>
  await instance.get(`write-ups/search/by-year-and-location`, {
    params: { year, locationCode }
  });

// GET BY UNIQUE CONSTRAINT
export const getWriteUpByUniqueConstraint = async ({ locationCode, year, sectionCommented }) =>
  await instance.get(`write-ups/search/by-unique-constraint`, {
    params: { locationCode, year, sectionCommented }
  });

// DELETE
export const deleteWriteUp = async (id) =>
  await instance.delete(`write-ups/${id}`);

/* =========================================================
   React Query Hooks Write Up
   ========================================================= */

// GET ALL
export function useGetAllWriteUps() {
  return useQuery(
    ["getAllWriteUps"],
    getAllWriteUps,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups",
        }),
    }
  );
}

// GET BY ID
export function useGetWriteUpById(id) {
  return useQuery(
    ["getWriteUpById", id],
    () => getWriteUpById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write up",
        }),
    }
  );
}

// GET BY USERNAME
export function useGetWriteUpByUsername(username) {
  return useQuery(
    ["getWriteUpByUsername", username],
    () => getWriteUpByUsername(username),
    {
      enabled: !!username,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by username",
        }),
    }
  );
}

// GET BY USER ROLE
export function useGetWriteUpByUserRole(userRole) {
  return useQuery(
    ["getWriteUpByUserRole", userRole],
    () => getWriteUpByUserRole(userRole),
    {
      enabled: !!userRole,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by user role",
        }),
    }
  );
}

// GET BY TYPE
export function useGetWriteUpByType(type) {
  return useQuery(
    ["getWriteUpByType", type],
    () => getWriteUpByType(type),
    {
      enabled: !!type,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by type",
        }),
    }
  );
}

// GET BY LOCATION CODE
export function useGetWriteUpByLocationCode(locationCode) {
  return useQuery(
    ["getWriteUpByLocationCode", locationCode],
    () => getWriteUpByLocationCode(locationCode),
    {
      enabled: !!locationCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by location",
        }),
    }
  );
}

// GET BY YEAR
export function useGetWriteUpByYear(year) {
  return useQuery(
    ["getWriteUpByYear", year],
    () => getWriteUpByYear(year),
    {
      enabled: !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by year",
        }),
    }
  );
}

// GET BY YEAR AND LOCATION
export function useGetWriteUpByYearAndLocation(params) {
  return useQuery(
    ["getWriteUpByYearAndLocation", params],
    () => getWriteUpByYearAndLocation(params),
    {
      enabled: !!params?.year && !!params?.locationCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write ups by year and location",
        }),
    }
  );
}

// GET BY UNIQUE CONSTRAINT
export function useGetWriteUpByUniqueConstraint(params) {
  return useQuery(
    ["getWriteUpByUniqueConstraint", params],
    () => getWriteUpByUniqueConstraint(params),
    {
      enabled: !!params?.locationCode && !!params?.year && !!params?.sectionCommented,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching write up by unique constraint",
        }),
    }
  );
}

// CREATE
export function useCreateWriteUp() {
  const queryClient = useQueryClient();

  return useMutation(createWriteUp, {
    onSuccess: () => {
      notification.success({
        message: "Write up created successfully",
      });

      queryClient.invalidateQueries(["getAllWriteUps"]);
      queryClient.invalidateQueries(["getWriteUpByUsername"]);
      queryClient.invalidateQueries(["getWriteUpByUserRole"]);
      queryClient.invalidateQueries(["getWriteUpByType"]);
      queryClient.invalidateQueries(["getWriteUpByLocationCode"]);
      queryClient.invalidateQueries(["getWriteUpByYear"]);
      queryClient.invalidateQueries(["getWriteUpByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating write up",
      }),
  });
}

// CREATE BULK
export function useCreateWriteUps() {
  const queryClient = useQueryClient();

  return useMutation(createWriteUps, {
    onSuccess: () => {
      notification.success({
        message: "Write ups created successfully",
      });

      queryClient.invalidateQueries(["getAllWriteUps"]);
      queryClient.invalidateQueries(["getWriteUpByUsername"]);
      queryClient.invalidateQueries(["getWriteUpByUserRole"]);
      queryClient.invalidateQueries(["getWriteUpByType"]);
      queryClient.invalidateQueries(["getWriteUpByLocationCode"]);
      queryClient.invalidateQueries(["getWriteUpByYear"]);
      queryClient.invalidateQueries(["getWriteUpByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating write ups",
      }),
  });
}

// UPDATE
export function useUpdateWriteUp() {
  const queryClient = useQueryClient();

  return useMutation(updateWriteUp, {
    onSuccess: () => {
      notification.success({
        message: "Write up updated successfully",
      });

      queryClient.invalidateQueries(["getAllWriteUps"]);
      queryClient.invalidateQueries(["getWriteUpById"]);
      queryClient.invalidateQueries(["getWriteUpByUsername"]);
      queryClient.invalidateQueries(["getWriteUpByUserRole"]);
      queryClient.invalidateQueries(["getWriteUpByType"]);
      queryClient.invalidateQueries(["getWriteUpByLocationCode"]);
      queryClient.invalidateQueries(["getWriteUpByYear"]);
      queryClient.invalidateQueries(["getWriteUpByYearAndLocation"]);
      queryClient.invalidateQueries(["getWriteUpByUniqueConstraint"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating write up",
      }),
  });
}

// DELETE
export function useDeleteWriteUp() {
  const queryClient = useQueryClient();

  return useMutation(deleteWriteUp, {
    onSuccess: () => {
      notification.success({
        message: "Write up deleted successfully",
      });

      queryClient.invalidateQueries(["getAllWriteUps"]);
      queryClient.invalidateQueries(["getWriteUpByUsername"]);
      queryClient.invalidateQueries(["getWriteUpByUserRole"]);
      queryClient.invalidateQueries(["getWriteUpByType"]);
      queryClient.invalidateQueries(["getWriteUpByLocationCode"]);
      queryClient.invalidateQueries(["getWriteUpByYear"]);
      queryClient.invalidateQueries(["getWriteUpByYearAndLocation"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error deleting write up",
      }),
  });
}
